import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia, UitkCardLink } from "uitk-react-cards";
import * as React from "react";
import { CardMediaOverallInfo } from "./CardMediaOverallInfo";
import { FeaturedReviewContent } from "./FeaturedReviewContent";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { observer } from "mobx-react";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { classNames } from "uitk-react-utils";
import { HotelCardProps } from "components/flexComponents/Hotels/typings";

export const HotelCard: React.FC<HotelCardProps> = observer(
  ({
    hotel,
    locationId,
    index,
    callToAction,
    context,
    CardContent,
    className,
    moduleName = "hotels",
    isMobile,
    showGuestRating,
    displayBadges,
    withBorder,
  }) => {
    const { hotelName } = hotel;
    const imageLayout = isMobile ? undefined : UitkCardImageLayout.LEFT_FULLBLEED;
    const cardMediaClasses = classNames(withBorder && "uitk-flat-border-top uitk-flat-border-bottom");

    return (
      <UitkCard className={classNames("HotelsCard", className)} imageLayout={imageLayout} withBorder={withBorder}>
        <UitkCardMedia className={cardMediaClasses}>
          <CardMediaOverallInfo hotel={hotel} context={context} index={index} displayBadges={displayBadges} />
        </UitkCardMedia>
        <UitkCardContent className="hotelCardContent">
          {CardContent && <CardContent />}
          <FeaturedReviewContent context={context} hotel={hotel} showGuestRating={showGuestRating} />
          <UitkCardLink>
            <TrackedLink
              data-testid={`hotelcard-link-${index}`}
              className="uitk-card-link"
              moduleName={moduleName}
              rfrr="card"
              rel={getHotelLinkRel(callToAction)}
              href={getHotelLink(hotel, locationId, context, callToAction)}
            >
              {hotelName}
            </TrackedLink>
          </UitkCardLink>
        </UitkCardContent>
      </UitkCard>
    );
  }
);

/**
 * In case of pinnedHSR or pinnedHSRDated, we need to generate a rel="nofollow" link
 * @param callToAction
 */
export const getHotelLinkRel = (callToAction?: string) => {
  return callToAction && callToAction.startsWith("pinnedHSR") ? "nofollow" : null;
};

/**
 * Utility method to either:
 * - use the new HotelSearchLinkBuilder if we are going to pinned HSR or
 * - otherwise use the link given by hotels-flex-module backend
 * @param hotel
 * @param context
 * @param callToAction
 */
export const getHotelLink = (
  hotel: Hotel,
  locationId: string,
  context: ExtendedContextStore,
  callToAction?: string
) => {
  const { isoCheckIn, isoCheckOut, hotelId, hotelInfositeUrl } = hotel;

  if (callToAction && callToAction.startsWith("pinnedHSR")) {
    return new HotelSearchLinkBuilder()
      .withRegionId(locationId)
      .withStartDate(isoCheckIn)
      .withEndDate(isoCheckOut)
      .withPinnedHotelId(hotelId)
      .build(context);
  }

  return hotelInfositeUrl;
};
